import React, { useState, useEffect } from 'react';
import { getMyCacsData, getCaCsSlots, updateCaCsSlots, getCaCsSpecialSlots, updateCaCsSpecialSlots } from '../../../controller/agent_data_controller.js';

// 12 fixed 2-hour slots
const SLOTS = Array.from({ length: 12 }, (_, i) => {
    const startH = i * 2;
    const endH = startH + 2;
    const fmt = (h) => `${h.toString().padStart(2, '0')}:00`;
    return { number: i + 1, label: `Slot ${i + 1}  (${fmt(startH)} – ${endH === 24 ? '00:00' : fmt(endH)})` };
});

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const ManageScheduleCACS = () => {
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [myId, setMyId] = useState(null);

    const [activeTab, setActiveTab] = useState('weekly');

    // Weekly: { Monday: Set([1,5,6]), Tuesday: Set([...]), ... }
    const [daySlots, setDaySlots] = useState(() =>
        Object.fromEntries(DAYS.map(d => [d, new Set()]))
    );

    // Special date slots: { '2026-02-24': Set([5, 6]), ... }
    const [specialSlotsByDate, setSpecialSlotsByDate] = useState({});
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const profile = await getMyCacsData();
            if (profile && profile.id) {
                setMyId(profile.id);

                // Fetch weekly slots: result = [ [day, slot_number], ... ]
                const slotsResult = await getCaCsSlots(profile.id);
                if (slotsResult.message === 'success') {
                    const organized = Object.fromEntries(DAYS.map(d => [d, new Set()]));
                    slotsResult.result.forEach(([day, slotNum]) => {
                        if (organized[day]) organized[day].add(slotNum);
                    });
                    setDaySlots(organized);
                }

                // Fetch special slots: result = [ [date, slot_number], ... ]
                const specialResult = await getCaCsSpecialSlots(profile.id);
                if (specialResult.message === 'success') {
                    const byDate = {};
                    specialResult.result.forEach(([date, slotNum]) => {
                        const key = typeof date === 'string' ? date : new Date(date).toISOString().split('T')[0];
                        if (!byDate[key]) byDate[key] = new Set();
                        byDate[key].add(slotNum);
                    });
                    setSpecialSlotsByDate(byDate);
                }
            }
        } catch (err) {
            console.error('Error fetching schedule data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // Toggle a slot number in weekly schedule for a day
    const toggleWeeklySlot = (day, slotNum) => {
        setDaySlots(prev => {
            const updated = new Set(prev[day]);
            if (updated.has(slotNum)) updated.delete(slotNum);
            else updated.add(slotNum);
            return { ...prev, [day]: updated };
        });
    };

    const applyToAllWeekdays = (sourceDay) => {
        const sourceCopy = new Set(daySlots[sourceDay]);
        const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
        setDaySlots(prev => {
            const updated = { ...prev };
            weekdays.forEach(d => { updated[d] = new Set(sourceCopy); });
            return updated;
        });
    };

    const handleSaveSchedule = async () => {
        if (!myId) return;
        setUpdating(true);
        try {
            const flattenedSlots = [];
            DAYS.forEach(day => {
                daySlots[day].forEach(slotNum => {
                    flattenedSlots.push({ day, slot_number: slotNum });
                });
            });
            const result = await updateCaCsSlots(myId, flattenedSlots);
            if (result === 'success') alert('Weekly schedule updated successfully!');
            else alert('Failed to update weekly schedule');
        } catch (error) {
            console.error('Update schedule error:', error);
        } finally {
            setUpdating(false);
        }
    };

    // Toggle a slot for the selected special date
    const toggleSpecialSlot = (slotNum) => {
        setSpecialSlotsByDate(prev => {
            const current = new Set(prev[selectedDate] || []);
            if (current.has(slotNum)) current.delete(slotNum);
            else current.add(slotNum);
            return { ...prev, [selectedDate]: current };
        });
    };

    const handleSaveSpecialSchedule = async () => {
        if (!myId) return;
        setUpdating(true);
        try {
            const slotNumbers = Array.from(specialSlotsByDate[selectedDate] || []);
            const result = await updateCaCsSpecialSlots(myId, selectedDate, slotNumbers);
            if (result === 'success') alert(`Special schedule for ${selectedDate} updated successfully!`);
            else alert('Failed to update special schedule');
        } catch (error) {
            console.error('Update special schedule error:', error);
        } finally {
            setUpdating(false);
        }
    };

    const currentSpecialSet = specialSlotsByDate[selectedDate] || new Set();
    const definedSpecialDates = Object.keys(specialSlotsByDate).filter(d => specialSlotsByDate[d].size > 0).sort();

    if (loading) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading schedule...</div>;

    return (
        <div style={{ padding: '20px 40px' }}>
            <p className='title-demo'>Manage Schedule</p>
            <div className='content-div-demo' style={{ padding: '20px', marginTop: '20px' }}>
                {/* Tabs */}
                <div style={{ borderBottom: '1px solid #ddd', paddingBottom: '10px', marginBottom: '20px', display: 'flex', gap: '30px' }}>
                    {['weekly', 'special'].map(tab => (
                        <p key={tab} onClick={() => setActiveTab(tab)} style={{
                            fontSize: '18px', fontWeight: '600', cursor: 'pointer',
                            color: activeTab === tab ? 'blue' : 'black',
                            borderBottom: activeTab === tab ? '2px solid blue' : 'none',
                            paddingBottom: '5px', textTransform: 'capitalize'
                        }}>
                            {tab === 'weekly' ? 'Weekly Schedule' : 'Special Date Schedule'}
                        </p>
                    ))}
                </div>

                {activeTab === 'weekly' ? (
                    <div>
                        {DAYS.map(day => (
                            <div key={day} style={{ marginBottom: '25px', padding: '20px', border: '1px solid #e0e6ed', borderRadius: '12px', backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2d3748', margin: 0 }}>{day}</h3>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(day) && (
                                            <button
                                                onClick={() => applyToAllWeekdays(day)}
                                                style={{ padding: '6px 14px', fontSize: '13px', cursor: 'pointer', backgroundColor: '#4a5568', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', transition: 'background 0.2s' }}>
                                                Apply to all Weekdays
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setDaySlots(prev => ({ ...prev, [day]: new Set() }))}
                                            style={{ padding: '6px 14px', fontSize: '13px', cursor: 'pointer', backgroundColor: '#f56565', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600' }}>
                                            Clear All
                                        </button>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                                    {SLOTS.map(slot => {
                                        const isSelected = daySlots[day]?.has(slot.number);
                                        return (
                                            <div
                                                key={slot.number}
                                                onClick={() => toggleWeeklySlot(day, slot.number)}
                                                style={{
                                                    padding: '12px',
                                                    borderRadius: '10px',
                                                    cursor: 'pointer',
                                                    textAlign: 'center',
                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    backgroundColor: isSelected ? '#3182ce' : '#fff',
                                                    color: isSelected ? '#fff' : '#4a5568',
                                                    border: `2px solid ${isSelected ? '#3182ce' : '#e2e8f0'}`,
                                                    boxShadow: isSelected ? '0 4px 6px rgba(49, 130, 206, 0.2)' : 'none',
                                                    userSelect: 'none'
                                                }}
                                                onMouseOver={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#3182ce'; }}
                                                onMouseOut={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                            >
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', opacity: isSelected ? 0.9 : 0.6 }}>Slot {slot.number}</p>
                                                <p style={{ margin: '4px 0 0', fontSize: '14px', fontWeight: '600' }}>{slot.label.split('(')[1].replace(')', '')}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                                {(!daySlots[day] || daySlots[day].size === 0) && (
                                    <div style={{ textAlign: 'center', padding: '15px', marginTop: '10px', backgroundColor: '#fff5f5', borderRadius: '8px', border: '1px dashed #feb2b2' }}>
                                        <p style={{ fontSize: '14px', color: '#c53030', margin: 0, fontWeight: '500' }}>Unavailable for this day</p>
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className='submit-button' onClick={updating ? null : handleSaveSchedule}
                            style={{
                                marginTop: '10px', padding: '15px 30px', borderRadius: '10px', fontSize: '16px', fontWeight: '700',
                                backgroundColor: updating ? '#a0aec0' : '#3182ce', boxShadow: '0 4px 14px rgba(49, 130, 206, 0.3)'
                            }}>
                            {updating ? 'Saving Changes...' : 'Save Weekly Schedule'}
                        </div>
                    </div>
                ) : (
                    <div>
                        {/* Date picker + slot grid for special schedule */}
                        <div style={{ backgroundColor: '#edf2f7', padding: '25px', borderRadius: '15px', marginBottom: '30px' }}>
                            <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <div style={{ minWidth: '220px' }}>
                                    <p style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568', marginBottom: '8px' }}>Select Special Date</p>
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        style={{ padding: '12px', width: '100%', border: '2px solid #cbd5e0', borderRadius: '8px', fontSize: '15px', fontWeight: '500' }}
                                    />
                                    <div className='submit-button' onClick={updating ? null : handleSaveSpecialSchedule}
                                        style={{ marginTop: '15px', width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: updating ? '#a0aec0' : '#48bb78' }}>
                                        {updating ? 'Saving...' : 'Save for Date'}
                                    </div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '16px', marginBottom: '15px', fontWeight: '700', color: '#2d3748' }}>Availability for {selectedDate}</p>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                                        {SLOTS.map(slot => {
                                            const isSelected = currentSpecialSet.has(slot.number);
                                            return (
                                                <div
                                                    key={slot.number}
                                                    onClick={() => toggleSpecialSlot(slot.number)}
                                                    style={{
                                                        padding: '10px',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        textAlign: 'center',
                                                        transition: 'all 0.2s',
                                                        backgroundColor: isSelected ? '#48bb78' : '#fff',
                                                        color: isSelected ? '#fff' : '#4a5568',
                                                        border: `2px solid ${isSelected ? '#48bb78' : '#e2e8f0'}`,
                                                        userSelect: 'none'
                                                    }}
                                                >
                                                    <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', opacity: 0.8 }}>SLOT {slot.number}</p>
                                                    <p style={{ margin: '2px 0 0', fontSize: '13px', fontWeight: '600' }}>{slot.label.split('(')[1].replace(')', '')}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Defined special dates summary */}
                        <div>
                            <p style={{ fontWeight: '600', marginBottom: '10px' }}>Defined Special Schedules:</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                {definedSpecialDates.map(date => (
                                    <div key={date} onClick={() => setSelectedDate(date)} style={{
                                        padding: '10px', backgroundColor: selectedDate === date ? '#e3f2fd' : 'white',
                                        border: `1px solid ${selectedDate === date ? '#2196f3' : '#ddd'}`,
                                        borderRadius: '4px', cursor: 'pointer'
                                    }}>
                                        <p style={{ fontWeight: '600', fontSize: '14px' }}>{date}</p>
                                        <p style={{ fontSize: '12px', color: '#666' }}>{specialSlotsByDate[date].size} slot{specialSlotsByDate[date].size !== 1 ? 's' : ''}</p>
                                    </div>
                                ))}
                                {definedSpecialDates.length === 0 && (
                                    <p style={{ fontSize: '14px', color: '#888' }}>No special schedules defined.</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
